// Explorer-Ansicht für eine NOCH NICHT importierte Kundenakte.
//
// Datenquelle ist der Share-Index (`KundenaktenFile`), nicht SeaweedFS: die
// Dateien liegen noch auf dem Netzlaufwerk, das nicht in den Backend-Container
// gemountet ist. Es gibt also keine Blobs — die Ansicht ist READ-ONLY, ohne
// Öffnen und ohne Download. Genau das drückt sich dadurch aus, dass diese
// Komponente dem FileExplorer KEINE Aktions-Callbacks gibt: fehlt `onOpenFile`,
// ist die Dateizeile nicht klickbar (und sieht dank `docx-noclick` auch nicht
// so aus). Kein Sonderfall-Flag nötig.
//
// Der Baum wird vollständig im Client aus `relPath` gebaut:
// GET /kundenakten-folders/:id liefert die Dateiliste ohnehin ungepaged, die
// Daten sind also schon da — ein eigener Aggregations-Endpunkt wäre reine
// Zusatzarbeit.

import * as React from "react";
import { FileExplorer } from "../documents/explorer/FileExplorer";
import {
  buildTreeFromRelPaths,
  filesAt,
  foldersAt,
  normalizeFolderPath,
  type ExplorerFile,
  type FlatPathEntry,
} from "../documents/explorer/explorerModel";
import { Toast } from "../redesign/Toast";
import { type KundenaktenFile } from "./types";
import { useAkteAblage, type AkteAblageZiel } from "./akteAblage";

/** Ohne Virtualisierung im Repo: pro Ebene in Blöcken nachliefern, statt
 *  mehrere tausend Zeilen auf einmal zu rendern. */
const CHUNK = 200;

export const KundenaktenIndexExplorer: React.FC<{
  files: KundenaktenFile[];
  /** Beschriftung der Wurzel — hier der Ordnername am Share. */
  rootLabel: string;
  /** Wohin Gezogenes geht (siehe `akteAblage.tsx`). Ohne Angabe nimmt die Liste nichts an. */
  ablageZiel?: AkteAblageZiel;
}> = ({ files, rootLabel, ablageZiel }) => {
  const [path, setPath] = React.useState("");
  const [expandedPaths, setExpandedPaths] = React.useState<ReadonlySet<string>>(() => new Set<string>());
  const [shown, setShown] = React.useState(CHUNK);
  const [toast, setToast] = React.useState<string | null>(null);
  const uid = React.useId().replace(/:/g, "");
  const dnd = useAkteAblage(ablageZiel ?? { grund: "" }, path);

  const tree = React.useMemo(
    () =>
      buildTreeFromRelPaths(
        files.map<FlatPathEntry>((f) => ({
          id: f.id,
          relPath: f.relPath,
          fileName: f.fileName,
          sizeBytes: f.sizeBytes,
          isSchaden: f.isSchaden,
        })),
      ),
    [files],
  );

  // Beim Ordnerwechsel wieder von vorn zählen.
  React.useEffect(() => {
    setShown(CHUNK);
  }, [path]);

  const levelFiles = filesAt(tree, path);
  const visible = levelFiles.slice(0, shown) as ExplorerFile<FlatPathEntry>[];

  const navigate = (next: string) => {
    const p = normalizeFolderPath(next);
    setPath(p);
    if (p) {
      setExpandedPaths((prev) => {
        const s = new Set(prev);
        let acc = "";
        for (const seg of p.split("/").filter(Boolean)) {
          acc += seg + "/";
          s.add(acc);
        }
        return s;
      });
    }
  };

  const onToggleExpand = (p: string) => {
    setExpandedPaths((prev) => {
      const s = new Set(prev);
      if (s.has(p)) s.delete(p);
      else s.add(p);
      return s;
    });
  };

  return (
    <>
      <FileExplorer<FlatPathEntry>
        rootLabel={rootLabel}
        path={path}
        onNavigate={navigate}
        folders={foldersAt(tree, path)}
        files={visible}
        browsing
        treeChildren={(p) => foldersAt(tree, p)}
        expandedPaths={expandedPaths}
        onToggleExpand={onToggleExpand}
        hasMore={shown < levelFiles.length}
        // Auch die read-only-Variante MUSS onLoadMore setzen: nur in diesem
        // Zweig rendert die DataTable ihren eigenen Scroll-Container — den
        // brauchen der Sticky-Header und das Nachladen.
        onLoadMore={() => setShown((n) => n + CHUNK)}
        scrollContainerId={`kundenaktenScroll-${uid}`}
        emptyText="Keine Dateien in diesem Ordner."
        // KEINE Aktions-Callbacks ⇒ read-only.
        onInertFileClick={() =>
          setToast("Vorschau erst nach dem Import verfügbar — den Ordner dazu einem Kontakt zuweisen.")
        }
        {...(ablageZiel ? dnd.props : {})}
      />
      {ablageZiel ? dnd.anzeige : null}
      <Toast open={!!toast} message={toast ?? ""} onClose={() => setToast(null)} />
    </>
  );
};
